/**
 * The tuition agreement: the offer the office set (each child's price, any
 * discount or aid, and every payment with its date), the agreement's own
 * words, and a box and typed name to sign. Answers save as the guardian goes
 * (see useStepAnswers), and carry the offer being shown, so a signature for an
 * offer the office has since replaced is refused. A signed agreement reads back
 * what was signed.
 */
import { Link } from 'react-router-dom';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { formatChecklistDate } from '~/pages/FamilyDetail/checklistWords';
import { formatCurrency } from '~/utils/paymentUtils';
import type { AgreementContext } from '../useChecklistStep';
import AgreementChange from './AgreementChange';
import { AgreementTerms, AgreementTuition, changeWhoPath, Field, Section, StepFooter, StepHeader, type StepPaths, type StepProps } from './stepParts';
import { useStepAnswers } from './useStepAnswers';

interface AgreementAnswers {
  accepted: boolean;
  typedName: string;
  agreementId: string | null;
}

function readAnswers(answers: unknown, agreementId: string | null): AgreementAnswers {
  const given = answers && typeof answers === 'object' && !Array.isArray(answers) ? (answers as Record<string, unknown>) : {};
  return {
    accepted: given.accepted === true,
    typedName: typeof given.typedName === 'string' ? given.typedName : '',
    // Always the offer on the page, not one an earlier visit saved.
    agreementId,
  };
}

export default function AgreementStep({ step, readOnly, overviewPath, stepPath }: StepProps) {
  const agreement = step.agreement;
  if (!agreement) return null;

  return (
    <div className="space-y-6">
      {/* An offer carries the intro as it was offered, shown with its terms. */}
      <StepHeader step={agreement.agreementId && agreement.terms?.intro ? { ...step, intro: null } : step} />
      {agreement.agreementId ? (
        <>
          {agreement.isChange ? <AgreementChange agreement={agreement} /> : <Offer agreement={agreement} overviewPath={overviewPath} stepPath={stepPath} />}
          {readOnly ? (
            <Receipt typedName={readAnswers(step.answers, null).typedName} />
          ) : (
            <SignForm step={step} agreement={agreement} overviewPath={overviewPath} stepPath={stepPath} />
          )}
        </>
      ) : null}
    </div>
  );
}

function Offer({ agreement, overviewPath, stepPath }: { agreement: AgreementContext } & StepPaths) {
  // Who's coming is where the plan is chosen; it opens ready to change.
  const planPath = agreement.whoItemId ? changeWhoPath(stepPath, agreement.whoItemId) : overviewPath;

  return (
    <>
      {agreement.plan ? (
        <Section>
          <h2 className="text-lg font-semibold">{agreement.plan.name}</h2>
          {agreement.planCanChange && !agreement.signed ? (
            <Link to={planPath} className="inline-flex min-h-11 items-center text-primary underline underline-offset-2">
              Change your payment plan
            </Link>
          ) : null}
        </Section>
      ) : null}

      <AgreementTuition agreement={agreement} />

      <Section>
        <h2 className="text-lg font-semibold">Payments</h2>
        <ul className="divide-y">
          {agreement.payments.map((payment, index) => (
            <li key={index} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2">
              <span>{formatChecklistDate(payment.dueOn)}</span>
              <span className="tabular-nums">{formatCurrency(payment.amountCents)}</span>
              <span className="basis-full text-sm text-muted-foreground">{payment.autoPay ? 'Auto-pay' : 'Pay yourself'}</span>
            </li>
          ))}
        </ul>
      </Section>

      <AgreementTerms agreement={agreement} />
    </>
  );
}

function Receipt({ typedName }: { typedName: string }) {
  if (!typedName) return null;
  return (
    <Section>
      <p className="break-words">Signed by {typedName}</p>
    </Section>
  );
}

function SignForm({ step, agreement, overviewPath, stepPath }: {
  step: StepProps['step'];
  agreement: AgreementContext;
  overviewPath: string;
  stepPath: (itemId: string) => string;
}) {
  const itemId = step.item.id;
  const { answers, change, submit, saveState, errors, submitting } = useStepAnswers<AgreementAnswers>({
    itemId,
    initial: readAnswers(step.answers, agreement.agreementId ?? null),
    overviewPath,
    stepPath,
    // The offer's id goes with the first save or submit, even if nothing else changes.
    unsentAtStart: true,
  });

  return (
    <>
      <fieldset disabled={submitting} className="min-w-0 space-y-6">
        <Section>
          <div className="flex min-h-11 items-start gap-3">
            <Checkbox
              id={`${itemId}-accept`}
              className="mt-0.5 h-5 w-5"
              checked={answers.accepted}
              onCheckedChange={(value) => change((current) => ({ ...current, accepted: value === true }))}
            />
            <Label htmlFor={`${itemId}-accept`} className="text-base font-normal leading-snug">I accept the tuition agreement</Label>
          </div>
          <Field id={`${itemId}-name`} label="Type your full name">
            <Input
              id={`${itemId}-name`}
              autoComplete="name"
              className="h-11 md:h-10"
              value={answers.typedName}
              onChange={(event) => change((current) => ({ ...current, typedName: event.target.value }))}
            />
          </Field>
        </Section>
      </fieldset>
      <StepFooter errors={errors} saveState={saveState} submitting={submitting} label="Sign the agreement" onSubmit={submit} />
    </>
  );
}
